import type { ConnectorManifest } from '../../../lib/diagram/connector-manifest.types';
import { registerConnector } from '../../../lib/diagram/registries';
import { CORE_ROUTING_POLICY_IDS } from '../../../core/connectors/routing';
import { z } from 'zod';

const baseConnectorProps = z.object({
  color: z.string().default('#6b7280'),
  width: z.number().default(2),
  dashed: z.boolean().default(false),
  label: z.string().optional(),
});

type BaseConnectorProps = z.infer<typeof baseConnectorProps>;

function straightEdgeSvg(pts: number[], props: BaseConnectorProps): string {
  if (pts.length < 4) return '';
  // Points arrive as a flat [x0, y0, x1, y1, ...] array from the canvas.
  // Rehydrate into {x, y} tuples so the renderer stays readable.
  const points: Array<{ x: number; y: number }> = [];
  for (let i = 0; i < pts.length; i += 2) {
    points.push({ x: pts[i], y: pts[i + 1] });
  }
  const [start, ...rest] = points;
  const pathData = `M ${start.x} ${start.y} ` + rest.map((p) => `L ${p.x} ${p.y}`).join(' ');
  const dash = props.dashed ? ' stroke-dasharray="6 4"' : '';
  const end = rest[rest.length - 1];
  const prev = rest.length > 1 ? rest[rest.length - 2] : start;
  const angle = Math.atan2(end.y - prev.y, end.x - prev.x);
  const arrowSize = 8;
  const ax1 = end.x - arrowSize * Math.cos(angle - 0.4);
  const ay1 = end.y - arrowSize * Math.sin(angle - 0.4);
  const ax2 = end.x - arrowSize * Math.cos(angle + 0.4);
  const ay2 = end.y - arrowSize * Math.sin(angle + 0.4);
  const arrow = `<polygon points="${end.x},${end.y} ${ax1},${ay1} ${ax2},${ay2}" fill="${props.color}" />`;

  let labelSvg = '';
  if (props.label && points.length >= 2) {
    const mx = (start.x + end.x) / 2;
    const my = (start.y + end.y) / 2;
    labelSvg = `<text x="${mx}" y="${my - 6}" text-anchor="middle" font-size="11" fill="${props.color}">${props.label}</text>`;
  }

  return `<path d="${pathData}" stroke="${props.color}" stroke-width="${props.width}" fill="none"${dash} />${arrow}${labelSvg}`;
}

export const ContainmentConnectorManifest: ConnectorManifest<BaseConnectorProps> = {
  id: 'network_containment',
  version: '1.0.0',
  category: 'network',
  routingPolicyId: CORE_ROUTING_POLICY_IDS.networkTopology,
  label: 'Contains',
  iconId: 'box',
  propsSchema: baseConnectorProps,
  defaultProps: () => ({ color: '#6b7280', width: 1, dashed: false }),
  svg: (args) => straightEdgeSvg(args.points, args.props),
};

export const NetworkAccessConnectorManifest: ConnectorManifest<BaseConnectorProps> = {
  id: 'network_access',
  version: '1.0.0',
  category: 'network',
  routingPolicyId: CORE_ROUTING_POLICY_IDS.networkTopology,
  label: 'Network Access',
  iconId: 'shield',
  propsSchema: baseConnectorProps,
  defaultProps: () => ({ color: '#22c55e', width: 2, dashed: false }),
  svg: (args) => straightEdgeSvg(args.points, args.props),
};

export const DataFlowConnectorManifest: ConnectorManifest<BaseConnectorProps> = {
  id: 'network_data_flow',
  version: '1.0.0',
  category: 'network',
  routingPolicyId: CORE_ROUTING_POLICY_IDS.networkTopology,
  label: 'Data Flow',
  iconId: 'arrow-right',
  propsSchema: baseConnectorProps,
  defaultProps: () => ({ color: '#06b6d4', width: 2, dashed: false }),
  svg: (args) => straightEdgeSvg(args.points, args.props),
};

export const IdentityConnectorManifest: ConnectorManifest<BaseConnectorProps> = {
  id: 'network_identity',
  version: '1.0.0',
  category: 'network',
  routingPolicyId: CORE_ROUTING_POLICY_IDS.networkTopology,
  label: 'Assumes Role',
  iconId: 'key',
  propsSchema: baseConnectorProps,
  defaultProps: () => ({ color: '#f59e0b', width: 2, dashed: true }),
  svg: (args) => straightEdgeSvg(args.points, args.props),
};

export const RoutingConnectorManifest: ConnectorManifest<BaseConnectorProps> = {
  id: 'network_routing',
  version: '1.0.0',
  category: 'network',
  routingPolicyId: CORE_ROUTING_POLICY_IDS.networkTopology,
  label: 'Routes To',
  iconId: 'route',
  propsSchema: baseConnectorProps,
  defaultProps: () => ({ color: '#10b981', width: 2, dashed: false }),
  svg: (args) => straightEdgeSvg(args.points, args.props),
};

export const GovernanceConnectorManifest: ConnectorManifest<BaseConnectorProps> = {
  id: 'network_governance',
  version: '1.0.0',
  category: 'network',
  routingPolicyId: CORE_ROUTING_POLICY_IDS.networkTopology,
  label: 'Governance',
  iconId: 'lock',
  propsSchema: baseConnectorProps,
  defaultProps: () => ({ color: '#a855f7', width: 1, dashed: true }),
  svg: (args) => straightEdgeSvg(args.points, args.props),
};

export const NetworkConnectorManifests = [
  ContainmentConnectorManifest,
  NetworkAccessConnectorManifest,
  DataFlowConnectorManifest,
  IdentityConnectorManifest,
  RoutingConnectorManifest,
  GovernanceConnectorManifest,
];

export function registerNetworkConnectors() {
  for (const manifest of NetworkConnectorManifests) {
    registerConnector('network', manifest);
  }
}
